import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from './client';
import { todayString } from '../utils/dateUtils';
import { toOrderStatusQuery } from '../utils/orderStatusFilter';
import type { OrderItem, OrderFilters } from '../types';

/**
 * 订单模块 API（react-query 数据层）。
 * 统一约定：本文件只提供「查询键 + 查询/变更 hooks + 必要的模块函数」，
 * 页面组件通过 react-query 消费，禁止散落的直接 get/post 调用。
 */

// ============ 查询键 ============
export const ORDER_QUERY_KEY = ['orders'];
export const KANBAN_QUERY_KEY = ['kanban-orders'];

// ============ 查询 hooks ============

/** 订单列表（分页 + 筛选）；refreshTick 由页面手动递增触发刷新 */
export function useGetOrders(filters: OrderFilters, page: number, pageSize: number, refreshTick?: number) {
  return useQuery({
    queryKey: [...ORDER_QUERY_KEY, filters, page, pageSize, refreshTick],
    queryFn: async () => {
      // 状态那一格存的是分段名（默认「在途」），出网前换成后端的 orderStatus 重复键 + 排序键
      const { orderStatus, ...rest } = filters;
      const response = await get<{ data: OrderItem[], pagination: { page: number, pageSize: number, total: number } }>('/orders', {
        params: {
          ...rest,
          ...toOrderStatusQuery(orderStatus),
          page,
          pageSize
        }
      });
      return response;
    }
  });
}

/** 看板订单 */
export function useGetKanbanOrders() {
  return useQuery({
    queryKey: KANBAN_QUERY_KEY,
    queryFn: async () => {
      const response = await get<OrderItem[]>('/orders/kanban');
      return response;
    }
  });
}

// ============ 客户档案页的订单读数 ============

/**
 * `GET /orders/customer-summary` 的出参：一位客户名下的核算读数，全部由服务端一次分组扫表算出。
 *
 * 为什么读数不能在这儿由页面自己 reduce：列表端点有 `pageSize` 封顶，
 * 客户一旦有几百上千张单，第 201 张之后就不参与求和了——页面给出一个看着合理其实偏小的欠款数。
 * 「待收/待付」两格与首页看板、核算月报走的是同一条 `moneyAggregateSql`（逐单夹零、排除已取消与已拒），
 * 所以这里出来的数和核算页对得上；页面侧不再留第二份算法。
 */
export interface CustomerOrderSummary {
  /** 参与核算的单数（已取消、已拒绝不在内） */
  validOrders: number;
  incomeOrders: number;
  expenseOrders: number;
  /** 待收款：该客户名下收入订单的逐单余款合计 */
  incomePending: number;
  /** 待付款：该客户名下支出订单的逐单余款合计 */
  expensePending: number;
  /**
   * 已轧差抵消的**历史累计**（已结算批次里落在该客户订单上的抵消额之和）。
   * 它不参与上面两个余款格的加减——结算时那批单的已付金额已被抬高，再减一次就是双重抵扣。
   */
  nettingOffset: number;
}

export const CUSTOMER_ORDER_SUMMARY_QUERY_KEY = ['customer-order-summary'];

/** 客户名下的核算读数。刻意不挂功能键：会员墙关的是导出那一下点击，读数不是特权。 */
export function useCustomerOrderSummary(customerId: string | null | undefined) {
  return useQuery({
    queryKey: [...CUSTOMER_ORDER_SUMMARY_QUERY_KEY, customerId],
    queryFn: () => get<CustomerOrderSummary>('/orders/customer-summary', { params: { customerId } }),
    enabled: !!customerId,
  });
}

/** 客户订单区每个方向显示的上限：再多就指向订单列表页看 */
export const CUSTOMER_ORDERS_PREVIEW_MAX = 10;

export const CUSTOMER_ORDERS_QUERY_KEY = ['customer-upcoming-orders'];

/**
 * 客户档案页列表：该客户名下**未执行**的订单，按预约日期升序取最近若干条。
 *
 * `orderCategory` 传的是**当前登录用户视角下**的分类名，服务端做视角翻转（见 list-orders-query.dto.ts），
 * 所以「支出订单」这一档拿回来的正是页面上显示为支出订单的那批单。
 * 待审核的单会在结果里出现——档案页要看自己名下的全部在途单，与台账导出的闸门是两件事。
 */
export function useCustomerUpcomingOrders(customerId: string | null | undefined, orderCategory: string, enabled: boolean) {
  return useQuery({
    queryKey: [...CUSTOMER_ORDERS_QUERY_KEY, customerId, orderCategory],
    queryFn: async () => {
      const res = await get<{ data: OrderItem[] }>('/orders', {
        params: {
          customerId,
          orderCategory,
          orderStatus: '未执行',
          orderBy: 'orderStartDate',
          orderDirection: 'asc',
          pageSize: CUSTOMER_ORDERS_PREVIEW_MAX,
        },
      });
      return res?.data ?? [];
    },
    enabled: !!customerId && enabled,
  });
}

// ============ 状态变更时间线 ============

/** 共享查询键前缀：状态一经变更（看板拖拽 / 抽屉里的取消归档）按前缀失效重取 */
export const ORDER_STATUS_LOGS_QUERY_KEY = ['order-status-logs'];

/**
 * `GET /orders/:id/status-logs` 的一行。服务端已按记录时间倒序、上限 50 条，
 * 前端不再排序——两端各排一次会出现「谁后跑谁说了算」。
 */
export interface OrderStatusLogItem {
  id: string;
  oldStatus: string;
  newStatus: string;
  /** 操作人姓名。关联不到 Party 行时后端给的是**空串**而不是 null，展示层按「不署名」处理 */
  changedBy: string;
  changedById: string;
  /** 没有原因的变更服务端兜底成「状态变更」，所以这句永不为空 */
  changeReason: string;
  createdAt: string;
}

/**
 * 订单状态时间线（三端详情页同一条链路）。
 *
 * 失败态必须原样交给页面渲染：时间线的空态是「这张单还没走过状态变更」，
 * 把它当成空数组返回等于把「读不到」伪装成「没发生」。
 */
export function useOrderStatusLogs(orderId: string | null | undefined) {
  return useQuery({
    queryKey: [...ORDER_STATUS_LOGS_QUERY_KEY, orderId],
    enabled: !!orderId,
    queryFn: async () => {
      const data = await get<OrderStatusLogItem[]>(`/orders/${orderId}/status-logs`);
      return data ?? [];
    }
  });
}

// ============ 违约风险提示 ============

/** 与状态时间线同一条失效链：取消一次，风险计数就变一次 */
export const ORDER_CANCEL_RISK_QUERY_KEY = ['order-cancel-risk'];

/**
 * `GET /orders/:id/cancel-risk` 的读数：这张单的**提交方**在回看窗口内的取消情况。
 * 窗口、阈值都读自管理端「系统设置 · 订单策略」，改配置不用动这里。
 */
export interface OrderCancelRisk {
  windowDays: number;
  threshold: number;
  cancelledOrders: number;
  replacedByNewOrder: number;
  breachCount: number;
  atRisk: boolean;
  subjectName: string;
  /** 达阈值才有一句完整文案，未达阈值是空串 */
  message: string;
}

/**
 * 审核那一刻要看的风险数：判据全在服务端（口径见 orders.service.ts 的 cancelRiskOf），
 * 这里只负责「谁有得审才去读」——非审核方读了也只是四行数字，白搭一次请求。
 */
export function useOrderCancelRisk(orderId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...ORDER_CANCEL_RISK_QUERY_KEY, orderId],
    enabled: enabled && !!orderId,
    queryFn: async () => {
      const data = await get<OrderCancelRisk>(`/orders/${orderId}/cancel-risk`);
      return data ?? null;
    }
  });
}

// ============ 今日备注提醒 ============

/**
 * 今日/逾期提醒条目（`GET /order-notes/reminders`）。
 * remindAt 后端出网恒为 'YYYY-MM-DD'，按字典序比「哪一天」即可，不要 new Date 解析。
 */
export interface OrderReminderItem {
  id: string;
  content: string;
  noteType: string;
  remindAt: string;
  orderId: string;
  orderNumber: string | null;
  customerName: string | null;
}

/** 共享查询键前缀：写备注、改订单状态后按前缀失效，首页卡与订单页面板一起重取 */
export const ORDER_REMINDERS_QUERY_KEY = ['order-reminders'];

/**
 * 今日提醒（含逾期）。此前各处消费各自用 useEffect 直接 get，挂载一次就再也不动——
 * 备注删了、订单取消了，面板还照着旧快照摆着。收进 react-query 后失效有处可使。
 *
 * [limit] 只给订单页的弹出面板用（服务端真实生效）；首页不带 = 全量。
 * [orderId] 给订单详情抽屉用，只看这一单的到期提醒。
 */
export function useOrderReminders(opts: { limit?: number; orderId?: string } = {}) {
  const today = todayString();
  return useQuery({
    queryKey: [...ORDER_REMINDERS_QUERY_KEY, opts.orderId ?? 'all', opts.limit ?? 'all', today],
    queryFn: async () => {
      const params: Record<string, string | number> = { date: today };
      if (opts.limit) params.limit = opts.limit;
      if (opts.orderId) params.orderId = opts.orderId;
      const data = await get<OrderReminderItem[]>('/order-notes/reminders', { params });
      return data ?? [];
    }
  });
}

// ============ 被对方驳回、待我修改重提 ============

/**
 * 弹窗与首页提示行共用的预览条数。计数一律取服务端的 `pagination.total`，
 * 不拿这一页的长度当「共几张」——那是「待我审核」网格踩过的坑（pageSize 10 把计数封顶）。
 */
export const REJECTED_ORDERS_PREVIEW_MAX = 5;

export const REJECTED_ORDERS_QUERY_KEY = ['rejected-orders'];

/**
 * 「本次会话是否已经弹过被驳回清单」——开关放在查询键旁边，不放组件文件里，
 * 因为复位点是 authStore 的 clearSession（登出、刷新失败、水合失败都走那里）。
 * 这样账号密码、验证码、扫码三条登录入口自动都覆盖，不必每处补记一笔；
 * 整页刷新则随 JS 重新启动自然回到未弹状态。
 *
 * 用内存变量而不是 sessionStorage：后者会在刷新后继续抑制，与「每次登录都弹一次」
 * 的要求相反；而 SPA 内部来回切页不该反复打断，所以也不能完全不加闸门。
 */
let rejectedOrdersNoticePopped = false;

export function hasRejectedOrdersNoticePopped(): boolean {
  return rejectedOrdersNoticePopped;
}

export function markRejectedOrdersNoticePopped(): void {
  rejectedOrdersNoticePopped = true;
}

export function resetRejectedOrdersNoticePopup(): void {
  rejectedOrdersNoticePopped = false;
}

/**
 * 「我提交、被对方驳回、还需要我改」的订单。
 *
 * 判据不在前端拼：`orderVisibilityWhere` 已把「我作为客户位驳回的单」整批挡在响应外
 * （驳回等于「这张单不是我的」），所以带 `reviewStatus=已拒绝` 的列表拿回来的就是
 * 该我处理的那几张。提交方一旦编辑重提或改派客户，服务端把 `approval_status` 置回
 * 「待审核」，这一行就自动消失——**不需要任何"已提醒过"的标记位**。
 */
export function useRejectedOrders() {
  return useQuery({
    queryKey: REJECTED_ORDERS_QUERY_KEY,
    queryFn: async () => {
      const response = await get<{ data: OrderItem[], pagination: { page: number, pageSize: number, total: number } }>(
        '/orders',
        { params: { reviewStatus: '已拒绝', page: 1, pageSize: REJECTED_ORDERS_PREVIEW_MAX } },
      );
      return { orders: response?.data ?? [], total: response?.pagination?.total ?? 0 };
    }
  });
}

// ============ 变更 hooks ============

/**
 * 看板拖拽更新状态（PATCH 部分更新）。
 *
 * 失效清单必须与 `orders/page.tsx` 的 handleOrderUpdated 成套：统计卡、
 * 「待我审核」网格、即将到来的联系人都是**独立查询键**，只刷列表与看板就会出现
 * 「拖完状态，顶部数字还是旧的」。今日提醒同属下游（后端会按订单状态筛掉已取消/被拒的提醒），
 * 状态变更时间线也是——这次迁移本身就会在时间线上多出一行。
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => patch<OrderItem>(`/orders/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORDER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: KANBAN_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['orderStats'] });
      queryClient.invalidateQueries({ queryKey: ['pendingReviewOrders'] });
      queryClient.invalidateQueries({ queryKey: ['upcomingContacts'] });
      queryClient.invalidateQueries({ queryKey: ORDER_REMINDERS_QUERY_KEY });
      // 状态刚迁移完，服务端同一笔事务里就追加了一条时间线；详情抽屉若正开着必须重取
      queryClient.invalidateQueries({ queryKey: ORDER_STATUS_LOGS_QUERY_KEY });
    }
  });
}

// ============ 订单分享（尊享会员功能 share_order） ============

/**
 * 分享链接的产出全部在服务端：摘要文本、链接、有效期都来自 POST /orders/:id/share。
 * 此前客户端自己拼文案，四端各拼一份、字段口径跟着本地投影走，
 * 于是「分享出去的内容」和「列表页看到的内容」可以不一样。
 */
export interface OrderShareResult {
  shareUrl: string;
  summaryText: string;
  expiresAt: string;
}

/** 公开分享页的载荷：服务端白名单摘要，不含审核状态、审核留言、支付凭证等内部字段 */
export interface SharedOrderSummary {
  orderNumber: string | null;
  orderCategory: string | null;
  orderStatus: string | null;
  serviceTypeName: string | null;
  customerName: string | null;
  companyName: string | null;
  contactPerson: string | null;
  contactPhone: string | null;
  activityAddress: string | null;
  orderStartDate: string | null;
  orderEndDate: string | null;
  serviceTimeText: string;
  arrivalText: string | null;
  orderAmount: number;
  paidAmount: number;
  balance: number;
  paymentStatusText: string;
  paymentMethod: string | null;
  specialInstructions: string | null;
}

export interface SharedOrderView {
  summary: SharedOrderSummary;
  summaryText: string;
  expiresAt: string;
}

/**
 * 取该订单当前可用的分享链接（不存在则由服务端生成）。
 *
 * 用 query 而不是 mutation：弹窗一挂载就要拿到链接，且 StrictMode / 并发挂载会被
 * 同一 queryKey 合并成一次请求——连发两次 POST 会让后一次撤销前一次的链接，
 * 弹窗里留下的可能就是已被撤销的那条。
 * refetchOnWindowFocus 必须关：否则每次切回本页面都会重新生成并作废上一条链接。
 */
export function useOrderShareLink(orderId: string) {
  return useQuery({
    queryKey: ['order-share', orderId],
    queryFn: () => post<OrderShareResult>(`/orders/${orderId}/share`),
    enabled: !!orderId,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** 撤销有效链接。不要求会员身份：掉级后更应该还能关掉已发出去的公开入口 */
export function useRevokeOrderShare() {
  return useMutation({
    mutationFn: (orderId: string) => del<null>(`/orders/${orderId}/share`),
  });
}

/** 公开访问者按令牌读摘要（失败即 404「分享链接无效或已过期」，不重试） */
export function useSharedOrderView(token: string) {
  return useQuery({
    queryKey: ['order-share-view', token],
    queryFn: () => get<SharedOrderView>(`/share/order/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
  });
}
